import type { SVGProps } from 'react';

/**
 * Minimal icon set (Heroicons outline paths, 24x24, stroke 2). Pass `className` for size/colour,
 * e.g. <Plus className="w-4 h-4" />. Icons are decorative by default (aria-hidden); give the
 * surrounding button an aria-label.
 */
type IconProps = SVGProps<SVGSVGElement> & { title?: string };

function Icon({ d, title, className = 'w-4 h-4', ...rest }: IconProps & { d: string | string[] }) {
  const paths = Array.isArray(d) ? d : [d];
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title && <title>{title}</title>}
      {paths.map((p) => (
        <path key={p} d={p} />
      ))}
    </svg>
  );
}

export const Plus = (p: IconProps) => <Icon d="M12 4v16m8-8H4" {...p} />;
export const X = (p: IconProps) => <Icon d="M6 18L18 6M6 6l12 12" {...p} />;
export const Search = (p: IconProps) => <Icon d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" {...p} />;
export const ChevronDown = (p: IconProps) => <Icon d="M19 9l-7 7-7-7" {...p} />;
export const ChevronUp = (p: IconProps) => <Icon d="M5 15l7-7 7 7" {...p} />;
export const ChevronRight = (p: IconProps) => <Icon d="M9 5l7 7-7 7" {...p} />;
export const ChevronLeft = (p: IconProps) => <Icon d="M15 19l-7-7 7-7" {...p} />;
export const Check = (p: IconProps) => <Icon d="M5 13l4 4L19 7" {...p} />;
export const AlertTriangle = (p: IconProps) => (
  <Icon
    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
    {...p}
  />
);
export const Info = (p: IconProps) => <Icon d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" {...p} />;
export const ExternalLink = (p: IconProps) => (
  <Icon d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" {...p} />
);
export const Menu = (p: IconProps) => <Icon d="M4 6h16M4 12h16M4 18h16" {...p} />;
export const Download = (p: IconProps) => <Icon d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" {...p} />;
export const Pencil = (p: IconProps) => (
  <Icon d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" {...p} />
);
export const Trash = (p: IconProps) => (
  <Icon d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" {...p} />
);
export const Lock = (p: IconProps) => (
  <Icon d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" {...p} />
);
export const Refresh = (p: IconProps) => (
  <Icon d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" {...p} />
);
export const Filter = (p: IconProps) => (
  <Icon d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" {...p} />
);
export const DotsHorizontal = (p: IconProps) => <Icon d="M5 12h.01M12 12h.01M19 12h.01" {...p} />;
export const Sun = (p: IconProps) => (
  <Icon d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" {...p} />
);
export const Moon = (p: IconProps) => <Icon d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" {...p} />;
export const Monitor = (p: IconProps) => (
  <Icon d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" {...p} />
);
export const Inbox = (p: IconProps) => (
  <Icon d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" {...p} />
);
export const Home = (p: IconProps) => (
  <Icon d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" {...p} />
);
export const User = (p: IconProps) => (
  <Icon d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" {...p} />
);
export const Calendar = (p: IconProps) => (
  <Icon d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" {...p} />
);
